import { useState, useCallback, useEffect, useRef, useContext, useMemo } from 'react'
import { FC } from 'react'
import * as cn from 'classnames'
import * as styles from 'components/features/pfm/SegmentButton/PostShareButton.styl'
import { SegmentButton } from 'components/features/pfm/SegmentButton/SegmentButton'
import ShareIcon from 'icons/share.svg'
import { ShareButtonTooltip, FeedShareMethod } from 'components/features/pfm/Tooltip/ShareButtonTooltip'
import { copyToClipboard } from 'helpers/shareHelpers'
import { IS_STATUS_APP } from 'helpers/userAgentHelpers'
import { ShareableInfo } from 'interfaces/share'
import { OuterEventTrackerContext } from 'components/utils/OuterEventTracker'
import { TooltipDirection } from 'components/features/pfm/Tooltip/Tooltip'
import { generateShareUrl } from 'helpers/socialHelpers'

type PostShareButtonProps = {
  title: string
  content: string
  url: string
  hideIcon?: boolean

  onShare: (info: ShareableInfo) => void
}

const APPROX_SPACE_FOR_MENU = 180

export const PostShareButton: FC<PostShareButtonProps> = (props) => {
  const { title, content, url, hideIcon, onShare } = props
  const [tooltipDirection, setTooltipDirection] = useState<TooltipDirection | null>(null)
  const [linkCopied, setLinkCopied] = useState(false)

  const timeoutId = useRef<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const emailLinkRef = useRef<HTMLAnchorElement | null>(null)

  useEffect(() => {
    return () => {
      if (typeof timeoutId.current === 'number') {
        window.clearTimeout(timeoutId.current)
      }
    }
  }, [])

  const handleClick = useCallback(() => {
    if (IS_STATUS_APP) {
      onShare({
        method: 'native',
        title,
        content: `${ content }\n\n${ url }`,
      })

      return
    }

    const node = containerRef.current
    if (!node) {
      return
    }

    const topPageOffset = document.body.getBoundingClientRect().top + node.getBoundingClientRect().top
    setTooltipDirection(topPageOffset > APPROX_SPACE_FOR_MENU ? 'up' : 'down')
  }, [onShare, title, content, url])

  const emailShareInfo: ShareableInfo = useMemo(() => ({
    method: 'email',
    subject: title,
    body: `${ content }\n\n${ url }`,
  }), [content, title, url])

  const handleShare = useCallback((method: FeedShareMethod) => {
    switch (method) {
      case 'copy':
        copyToClipboard(url)
        setLinkCopied(true)

        timeoutId.current = window.setTimeout(() => {
          setTooltipDirection(null)
          setLinkCopied(false)
        }, 1000)

        return

      case 'facebook':
        onShare({
          method,
          url,
        })

        break

      case 'twitter':
        onShare({
          method,
          text: content,
          url,
        })

        break

      case 'email':
        if (emailLinkRef.current) {
          emailLinkRef.current.click()
        }

        onShare(emailShareInfo)

        break
    }

    setTooltipDirection(null)

  }, [content, emailShareInfo, onShare, url])

  const tooltipContainerClassName = cn(styles.tooltipContainer, {
    [styles.openedUp]: tooltipDirection === 'up',
    [styles.openedDown]: tooltipDirection === 'down',
  })

  const tooltipRef = useRef<HTMLDivElement>(null)
  const outerEventTracker = useContext(OuterEventTrackerContext)

  const onHide = useCallback(() => {
    setTooltipDirection(null)
  }, [setTooltipDirection])

  useEffect(() => {
    if (!tooltipRef.current) {
      return
    }

    outerEventTracker.addOuterEventListener('click tap', onHide, tooltipRef.current)

    return () => outerEventTracker.removeOuterEventListener('click tap', onHide)
  }, [onHide, outerEventTracker])

  const emailShareUrl = generateShareUrl(emailShareInfo)

  return (
    <div className={ styles.postShareButton } ref={ containerRef }>
      <a
        href={ emailShareUrl }
        className={ styles.emailLink }
        ref={ emailLinkRef }
      />
      <div
        className={ tooltipContainerClassName }
        ref={ tooltipRef }
      >
        <ShareButtonTooltip
          linkCopied={ linkCopied }
          onShare={ handleShare }
          direction={ tooltipDirection || 'up' }
        />
      </div>
      <SegmentButton
        pressed={ false }
        // analytics is sent by an epic reacting on `REGISTER_SHARE` action
        analyticsAction={ null }
        onClick={ handleClick }
      >
        <SegmentButton.Segment>
          { !hideIcon && (
            <SegmentButton.Icon size={ 16 } src={ ShareIcon } />
          )}
          <SegmentButton.Text text='Share' />
        </SegmentButton.Segment>
      </SegmentButton>
    </div>
  )
}
